import type { OpenItemPerson, PersonRef } from '../api/dto.js';
import type { Principal } from '../access/access.js';
import { memberCan } from '../access/access.js';

/**
 * Open item scope (decision O1): `null` = internal (members only), else a share link.
 * Members see every scope; guests only their own link.
 */
export function canSeeOpenItem(p: Principal, item: { linkId: string | null }): boolean {
  return p.kind === 'member' || item.linkId === p.linkId;
}

/**
 * Decisions O4/U6 (trusted circle): everyone who may write can create, edit, reassign, resolve,
 * reopen, delete and reply – only member viewers and view guests are read-only.
 */
export function canWriteOpenItems(p: Principal): boolean {
  return p.kind === 'member' ? memberCan(p.role, 'board.edit') : p.access !== 'view';
}

export function canEditOpenItem(p: Principal, item: { linkId: string | null }): boolean {
  return canSeeOpenItem(p, item) && canWriteOpenItems(p);
}

/** The scope a new item gets: guests always create in their own link, members choose. */
export function openItemScopeFor(
  p: Principal,
  requested: string | null | undefined,
): string | null {
  return p.kind === 'guest' ? p.linkId : (requested ?? null);
}

export interface ScopedGuest extends OpenItemPerson {
  kind: 'guest';
  linkId: string;
}

export interface BoardPeople {
  members: readonly OpenItemPerson[];
  guests: readonly ScopedGuest[];
}

const byName = (a: OpenItemPerson, b: OpenItemPerson) =>
  a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

/**
 * Who may be assigned (or originate) in a scope (decision O1): the board's members plus the
 * guests of the same link – never guests of other links. Members first, each sorted by name.
 */
export function assigneeCandidates(scope: string | null, people: BoardPeople): OpenItemPerson[] {
  const guests = people.guests.filter((g) => scope !== null && g.linkId === scope);
  return [
    ...people.members
      .map((m) => ({ kind: 'member' as const, id: m.id, name: m.name }))
      .sort(byName),
    ...guests.map((g) => ({ kind: 'guest' as const, id: g.id, name: g.name })).sort(byName),
  ];
}

/** The candidate a reference points at, or `undefined` when it is outside the scope. */
export function findCandidate(
  candidates: readonly OpenItemPerson[],
  ref: PersonRef,
): OpenItemPerson | undefined {
  return candidates.find((c) => c.kind === ref.kind && c.id === ref.id);
}
